import React, { useState, useEffect, useCallback } from "react";
import moment from "moment";
import { useHookstate } from "@hookstate/core";
import globalAuthStateStore from "../../../../../stores/globalAuthStateStore";
import { VirtualLibrarianService } from "../../../../../services/VirtualLibrarianService";
import { FormatUTCDate, FormatUTCDateTime } from "../../../../../utilities/DateFormater";
import { downloadCsv } from "../../../../../utilities/CsvExport";
import AccordionTable from "./AccordionTable/table";
import VirtualLibrarianChatLogFilter from "./filter";
import ButtonControl from "../../../../../components/controls/ButtonControl";

const VirtualLibrarianChatLog = () => {
  const [chatLogs, setChatLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const authStore = useHookstate(globalAuthStateStore);
  const vlService = React.useMemo(
    () => new VirtualLibrarianService(authStore),
    [authStore]
  );

  const fetchData = useCallback(
    async (startDate, endDate) => {
      setLoading(true);
      const result = await vlService.getAllChatLogs(startDate, endDate);
      if (result?.Success) {
        const formattedData = (result?.Data ?? []).map((f) => {
          return {
            Date: FormatUTCDate(f.StartDate),
            ODS: f.Organization + "\r\n" + (f.District ?? "") + "\r\n" + (f.School ?? ""),
            Organization: f.Organization,
            District: f.District,
            School: f.School,
            UserPosition: f.UserPosition,
            InitialQuestion: f.Chats[0] !== undefined ? f.Chats[0].Prompt : "",
            SessionId: f.SessionId,
            User: f.UserName,
            DisplayName: f.DisplayName,
            Chats: f.Chats,
          };
        });
        setChatLogs(formattedData);
      } else {
        setChatLogs([]);
      }
      setLoading(false);
    },
    [vlService]
  );

  useEffect(() => {
    // Default view: last 45 days, matching the API's own default when no
    // date range is provided.
    fetchData();
  }, [fetchData]);

  const headers = [
    { title: "Date", width: "8%" },
    { title: "Initial Question", width: "30%" },
    { title: "SessionId", width: "12%" },
    { title: "User", width: "18%" },
    { title: "UserPosition", width: "10%" },
    { title: "ODS", width: "15%" },
  ];

  const handleDownloadCsv = () => {
    const csvHeaders = [
      "RowNum",
      "Date",
      "Prompt",
      "Response",
      "Citations",
      "SessionId",
      "ChatId",
      "UserName",
      "DisplayName",
      "UserPosition",
      "Organization",
      "District",
      "School",
    ];
    let rowNum = 0;
    const rows = chatLogs.flatMap((log) =>
      (log.Chats ?? []).map((chat) => {
        rowNum += 1;
        const citations = (chat.Citations ?? [])
          .map((citation) => citation.document_name)
          .filter(Boolean)
          .join("; ");
        return [
          rowNum,
          FormatUTCDateTime(chat.Date),
          chat.Prompt,
          chat.Response,
          citations,
          log.SessionId,
          chat.ChatId,
          log.User,
          log.DisplayName,
          log.UserPosition,
          log.Organization,
          log.District,
          log.School,
        ];
      })
    );
    downloadCsv(`vl-admin-review_${moment().format("YYYY-MM-DD_HHmmss")}.csv`, csvHeaders, rows);
  };

  return (
    <div className="vl-content-wrapper">
      <h4>Admin Review</h4>
      <div className="vl-history-toolbar">
        <VirtualLibrarianChatLogFilter onApply={fetchData} loading={loading} />
        <ButtonControl
          className="btn-download-csv"
          disabled={chatLogs.length === 0}
          disabledTitle="Search for records before downloading."
          onClick={handleDownloadCsv}
        >
          Download CSV
        </ButtonControl>
      </div>
      <AccordionTable headers={headers} data={chatLogs} />
    </div>
  );
};

export default VirtualLibrarianChatLog;
